
import { Validator, AbstractControl } from '@angular/forms';
import { ValidationErrors } from '@angular/forms';
import { removeNonDigitsCNPJ } from 'src/app/helpers/utils/cpf-cnpj.util';

export class CpfCnpjValidator implements Validator {

    static cpfLength = 11;
    static cnpjLength = 14;

    static validate(control: AbstractControl): { equalDigits?: boolean; length?: boolean; invalidChecksum?: boolean } | null {
        const rawValue = (control.value || '');
        if (/\s/.test(rawValue)) {
            return { length: true };
        }
        const valueCNPJ = removeNonDigitsCNPJ(rawValue);
        if (!valueCNPJ) return null;

        if (CpfCnpjValidator.hasLetters(valueCNPJ)) {
            return CpfCnpjValidator.validateAlphaCnpj(valueCNPJ);
        }
        return CpfCnpjValidator.validateNumericCpfCnpj(valueCNPJ);
    }

    static validateCnpjOnly(control: AbstractControl): { equalDigits?: boolean; length?: boolean; invalidChecksum?: boolean; cpfNotAllowed?: boolean } | null {
        const rawValue = (control.value || '');
        if (/\s/.test(rawValue)) {
            return { length: true };
        }
        const value = removeNonDigitsCNPJ(rawValue);
        if (!value) return null;

        if (!CpfCnpjValidator.hasLetters(value) && value.length === CpfCnpjValidator.cpfLength) {
            return { cpfNotAllowed: true };
        }

        return CpfCnpjValidator.validate(control);
    }

    private static hasLetters(value: string): boolean {
        return /[A-Za-z]/.test(value);
    }

    private static isEqualDigits(value: string): boolean {
        return /^([A-Za-z0-9])\1+$/.test(value);
    }

    private static isEqualDigitsNumeric(value: string): boolean {
        return /^(\d)\1+$/.test(value);
    }

    private static validateAlphaCnpj(value: string): { equalDigits?: boolean; length?: boolean } | null {
        if (value.length === CpfCnpjValidator.cnpjLength) {
            if (CpfCnpjValidator.isEqualDigits(value)) {
                return { equalDigits: true };
            }
            return null; // CNPJ alfanumérico válido — checksum não se aplica
        }
        return { length: true };
    }

    private static validateNumericCpfCnpj(value: string): { equalDigits?: boolean; length?: boolean; invalidChecksum?: boolean } | null {
        if (value.length === CpfCnpjValidator.cpfLength) {
            if (CpfCnpjValidator.isEqualDigitsNumeric(value)) {
                return { equalDigits: true };
            }
            if (!CpfCnpjValidator.validateCpfChecksum(value)) {
                return { invalidChecksum: true };
            }
            return null;
        }
        if (value.length === CpfCnpjValidator.cnpjLength) {
            if (CpfCnpjValidator.isEqualDigitsNumeric(value)) {
                return { equalDigits: true };
            }
            if (!CpfCnpjValidator.validateCnpjChecksum(value)) {
                return { invalidChecksum: true };
            }
            return null;
        }
        return { length: true };
    }

    private static validateCpfChecksum(cpf: string): boolean {
        let sum = 0;
        for (let i = 0; i < 9; i++) {
            sum += parseInt(cpf[i]) * (10 - i);
        }
        let remainder = sum % 11;
        const digit1 = remainder < 2 ? 0 : 11 - remainder;
        if (digit1 !== parseInt(cpf[9])) return false;

        sum = 0;
        for (let i = 0; i < 10; i++) {
            sum += parseInt(cpf[i]) * (11 - i);
        }
        remainder = sum % 11;
        const digit2 = remainder < 2 ? 0 : 11 - remainder;
        return digit2 === parseInt(cpf[10]);
    }

    private static validateCnpjChecksum(cnpj: string): boolean {
        const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
        let sum = 0;
        for (let i = 0; i < 12; i++) {
            sum += parseInt(cnpj[i]) * weights1[i];
        }
        let remainder = sum % 11;
        const digit1 = remainder < 2 ? 0 : 11 - remainder;
        if (digit1 !== parseInt(cnpj[12])) return false;

        const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
        sum = 0;
        for (let i = 0; i < 13; i++) {
            sum += parseInt(cnpj[i]) * weights2[i];
        }
        remainder = sum % 11;
        const digit2 = remainder < 2 ? 0 : 11 - remainder;
        return digit2 === parseInt(cnpj[13]);
    }

    /**
    * Implementa a interface de um validator.
    */
    validate(c: AbstractControl): ValidationErrors | null {
        return CpfCnpjValidator.validate(c);
    }
}
